/* eslint-disable react/jsx-key */
import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Form,
  FormGroup,
  Label,
  Row,
  Table,
  UncontrolledCollapse,
  Spinner,
  Alert,
} from "reactstrap";
import DatePicker from "react-datepicker";
import moment from "moment";
import { toast } from "react-toastify";

import AppLayout from "layout/AppLayout";
import axios from "axiosClient";
import { useToken } from "auth/useToken";

import "react-datepicker/dist/react-datepicker.css";

const safeLen = (value) => (Array.isArray(value) ? value.length : 0);

const Section = ({ id, title, right, children }) => {
  return (
    <div className="mb-3 border rounded">
      <Button
        className="p-3 font-weight-bold w-100 text-left d-flex align-items-center justify-content-between"
        color="link"
        id={id}
        style={{ textDecoration: "none" }}
      >
        <span style={{ fontSize: "1.05rem" }}>{title}</span>
        {right ? <span className="ml-2">{right}</span> : null}
      </Button>
      <UncontrolledCollapse toggler={`#${id}`}>
        <div className="p-3 pt-0">{children}</div>
      </UncontrolledCollapse>
    </div>
  );
};

const KeyValueTable = ({ rows }) => {
  if (!rows?.length) return null;

  return (
    <Table bordered size="sm" className="mb-0">
      <tbody>
        {rows.map((r) => (
          <tr>
            <td className="text-muted" style={{ width: 220 }}>
              {r.label}
            </td>
            <td>{r.value}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const CodesTable = ({ items, showMessage }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <div className="text-muted">None</div>;
  }

  return (
    <div style={{ maxHeight: 320, overflow: "auto" }}>
      <Table striped size="sm" className="mb-0">
        <thead>
          <tr>
            <th style={{ width: showMessage ? 180 : "auto" }}>Code</th>
            {showMessage ? <th>Message</th> : null}
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={`${item?.code || "code"}_${idx}`}>
              <td>{item?.code ?? "-"}</td>
              {showMessage ? <td>{item?.message ?? "-"}</td> : null}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const SkippedTable = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <div className="text-muted">None</div>;
  }

  return (
    <div style={{ maxHeight: 320, overflow: "auto" }}>
      <Table striped size="sm" className="mb-0">
        <thead>
          <tr>
            <th style={{ width: 200 }}>Enquiry ID</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={`${item?.enquiryId || "enquiry"}_${idx}`}>
              <td>{item?.enquiryId ?? "-"}</td>
              <td>{item?.reason ?? "-"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const SmartupBulkImport = () => {
  const [token] = useToken();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const dateString = useMemo(
    () => moment(selectedDate).format("YYYY-MM-DD"),
    [selectedDate],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const result = await axios.post(
        "/api/payments-smartup-bulk",
        { date: dateString },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setResponse(result.data);

      const hasAnyErrors =
        safeLen(result.data?.smartup?.naturalPersons?.errors) > 0 ||
        safeLen(result.data?.smartup?.contracts?.errors) > 0 ||
        safeLen(result.data?.smartup?.bankOperations?.errors) > 0;

      if (result.data?.success) {
        toast.success(
          hasAnyErrors
            ? "Bulk import finished with some errors."
            : "Bulk import finished successfully.",
        );
      } else {
        toast.error("Bulk import did not complete successfully.");
      }
    } catch (err) {
      setError(err);

      const message =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        err?.message ||
        "Bulk import failed.";

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const summary = response?.summary;

  return (
    <AppLayout>
      <style>{`
        .border-left-primary { border-left: 4px solid #007bff !important; }
        .border-left-success { border-left: 4px solid #28a745 !important; }
        .border-left-info { border-left: 4px solid #17a2b8 !important; }
        .border-left-warning { border-left: 4px solid #ffc107 !important; }
      `}</style>
      <div className="dashboard-wrapper">
        <Card className="h-100 m-4 shadow">
          <CardBody>
            <CardTitle tag="h3" className="mb-4">
              Smartup Bulk Import
            </CardTitle>

            <Form onSubmit={handleSubmit}>
              <Row className="text-left align-items-end">
                <Col md="4">
                  <FormGroup>
                    <Label className="font-weight-bold">Select Date</Label>
                    <DatePicker
                      name="date"
                      autoComplete="off"
                      selected={selectedDate}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="YYYY-MM-DD"
                      onChange={(date) => setSelectedDate(date)}
                      className="form-control"
                      disabled={loading}
                    />
                    <small className="text-muted mt-1 d-block">
                      Will process: <strong>{dateString}</strong>
                    </small>
                  </FormGroup>
                </Col>

                <Col md="4" className="pb-3">
                  <Button
                    color="primary"
                    type="submit"
                    disabled={loading}
                    size="lg"
                    className="px-4"
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Processing...
                      </>
                    ) : (
                      "Run Bulk Import"
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>

            {error ? (
              <Alert color="danger" className="mt-4">
                <h5 className="alert-heading">Error Occurred</h5>
                <p className="mb-2">
                  {error?.response?.status
                    ? `HTTP ${error.response.status} - ${error?.response?.statusText || "Request failed"}`
                    : "Request failed"}
                </p>
                <hr />
                <details>
                  <summary style={{ cursor: "pointer" }} className="mb-2">
                    View error details
                  </summary>
                  <pre
                    className="mt-2 p-3 bg-white rounded"
                    style={{ overflow: "auto", fontSize: "0.85rem" }}
                  >
                    {JSON.stringify(
                      error?.response?.data || { message: error?.message },
                      null,
                      2,
                    )}
                  </pre>
                </details>
              </Alert>
            ) : null}

            {response ? (
              <div className="mt-4">
                <Alert
                  color={response.success ? "success" : "warning"}
                  className="mb-4"
                >
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <div>
                      <h5 className="mb-0">
                        {response.success
                          ? "Import Completed Successfully"
                          : "Import Completed with Issues"}
                      </h5>
                    </div>
                    <div className="mt-2 mt-md-0">
                      <Badge
                        color={
                          response.markedImported ? "success" : "secondary"
                        }
                        className="px-3 py-2 text-white"
                        style={{ fontSize: "0.9rem" }}
                      >
                        {response.markedImported
                          ? "Marked as Imported"
                          : "Not Marked"}
                      </Badge>
                    </div>
                  </div>
                </Alert>

                <Row className="mb-4">
                  <Col md="3" sm="6" className="mb-3">
                    <Card className="border-left-primary shadow-sm h-100">
                      <CardBody>
                        <div className="text-muted small">Total Payments</div>
                        <h3 className="mb-0">
                          {summary?.totalPaymentsFound ?? 0}
                        </h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3" sm="6" className="mb-3">
                    <Card className="border-left-success shadow-sm h-100">
                      <CardBody>
                        <div className="text-muted small">Bank Operations</div>
                        <h3 className="mb-0">
                          {summary?.eligibleBankOperations ?? 0}
                        </h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3" sm="6" className="mb-3">
                    <Card className="border-left-info shadow-sm h-100">
                      <CardBody>
                        <div className="text-muted small">Natural Persons</div>
                        <h3 className="mb-0">{summary?.naturalPersons ?? 0}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3" sm="6" className="mb-3">
                    <Card className="border-left-warning shadow-sm h-100">
                      <CardBody>
                        <div className="text-muted small">Skipped</div>
                        <h3 className="mb-0">
                          {summary?.skipped ?? safeLen(response?.skipped)}
                        </h3>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <div className="mt-3">
                  <Section
                    id="bulk_import_summary"
                    title="Summary"
                    right={
                      <Badge color="info" className="text-white">
                        {summary?.date ?? "-"}
                      </Badge>
                    }
                  >
                    <KeyValueTable
                      rows={[
                        {
                          label: "Date",
                          value: summary?.date ?? "-",
                        },
                        {
                          label: "Window Start",
                          value: summary?.window?.start
                            ? moment(summary.window.start).format(
                                "YYYY-MM-DD HH:mm:ss",
                              )
                            : "-",
                        },
                        {
                          label: "Window End",
                          value: summary?.window?.end
                            ? moment(summary.window.end).format(
                                "YYYY-MM-DD HH:mm:ss",
                              )
                            : "-",
                        },
                        {
                          label: "Total Payments Found",
                          value: summary?.totalPaymentsFound ?? "-",
                        },
                        {
                          label: "Eligible Bank Operations",
                          value: summary?.eligibleBankOperations ?? "-",
                        },
                        {
                          label: "Natural Persons",
                          value: summary?.naturalPersons ?? "-",
                        },
                        {
                          label: "Contracts",
                          value: summary?.contracts ?? "-",
                        },
                        {
                          label: "Skipped",
                          value: summary?.skipped ?? safeLen(response?.skipped),
                        },
                      ]}
                    />
                  </Section>

                  <Section
                    id="bulk_import_np"
                    title="Smartup Natural Persons"
                    right={
                      <>
                        <Badge color="success" className="mr-2 text-white">
                          OK:{" "}
                          {safeLen(
                            response?.smartup?.naturalPersons?.successes,
                          )}
                        </Badge>
                        <Badge color="danger" className="text-white">
                          Errors:{" "}
                          {safeLen(response?.smartup?.naturalPersons?.errors)}
                        </Badge>
                      </>
                    }
                  >
                    <Row>
                      <Col md="6">
                        <h6>Limits</h6>
                        <KeyValueTable
                          rows={Object.entries(
                            response?.smartup?.naturalPersons?.limits || {},
                          ).map(([k, v]) => ({
                            label: k,
                            value: v === "" ? "-" : String(v),
                          }))}
                        />
                      </Col>
                      <Col md="6">
                        <h6>Errors</h6>
                        <CodesTable
                          items={response?.smartup?.naturalPersons?.errors}
                          showMessage
                        />
                      </Col>
                    </Row>

                    <div className="mt-3">
                      <h6>Success Codes</h6>
                      <CodesTable
                        items={response?.smartup?.naturalPersons?.successes}
                      />
                    </div>
                  </Section>

                  <Section
                    id="bulk_import_contracts"
                    title="Smartup Contracts"
                    right={
                      <>
                        <Badge color="success" className="mr-2 text-white">
                          OK: {safeLen(response?.smartup?.contracts?.successes)}
                        </Badge>
                        <Badge color="danger" className="text-white">
                          Errors:{" "}
                          {safeLen(response?.smartup?.contracts?.errors)}
                        </Badge>
                      </>
                    }
                  >
                    <Row>
                      <Col md="6">
                        <h6>Limits</h6>
                        <KeyValueTable
                          rows={Object.entries(
                            response?.smartup?.contracts?.limits || {},
                          ).map(([k, v]) => ({
                            label: k,
                            value: v === "" ? "-" : String(v),
                          }))}
                        />
                      </Col>
                      <Col md="6">
                        <h6>Errors</h6>
                        <CodesTable
                          items={response?.smartup?.contracts?.errors}
                          showMessage
                        />
                      </Col>
                    </Row>

                    <div className="mt-3">
                      <h6>Success Codes</h6>
                      <CodesTable
                        items={response?.smartup?.contracts?.successes}
                      />
                    </div>
                  </Section>

                  <Section
                    id="bulk_import_bank_ops"
                    title="Smartup Bank Operations"
                    right={
                      <>
                        <Badge color="success" className="mr-2 text-white">
                          OK:{" "}
                          {safeLen(
                            response?.smartup?.bankOperations?.successes,
                          )}
                        </Badge>
                        <Badge color="danger" className="text-white">
                          Errors:{" "}
                          {safeLen(response?.smartup?.bankOperations?.errors)}
                        </Badge>
                      </>
                    }
                  >
                    <Row>
                      <Col md="6">
                        <h6>Limits</h6>
                        <KeyValueTable
                          rows={Object.entries(
                            response?.smartup?.bankOperations?.limits || {},
                          ).map(([k, v]) => ({
                            label: k,
                            value: v === "" ? "-" : String(v),
                          }))}
                        />
                      </Col>
                      <Col md="6">
                        <h6>Errors</h6>
                        <CodesTable
                          items={response?.smartup?.bankOperations?.errors}
                          showMessage
                        />
                      </Col>
                    </Row>

                    <div className="mt-3">
                      <h6>Success Codes</h6>
                      <CodesTable
                        items={response?.smartup?.bankOperations?.successes}
                      />
                    </div>
                  </Section>

                  <Section
                    id="bulk_import_skipped"
                    title="Skipped Payments"
                    right={
                      <Badge color="secondary" className="text-white">
                        {safeLen(response?.skipped)}
                      </Badge>
                    }
                  >
                    <SkippedTable items={response?.skipped} />
                  </Section>

                  <Section
                    id="bulk_import_raw"
                    title="Raw Response"
                    right={<Badge color="light">JSON</Badge>}
                  >
                    <pre
                      className="p-2 bg-light"
                      style={{ overflow: "auto", maxHeight: 420 }}
                    >
                      {JSON.stringify(response, null, 2)}
                    </pre>
                  </Section>
                </div>
              </div>
            ) : null}

            {!response && !error && !loading ? (
              <Alert color="info" className="mt-4">
                <div className="text-center py-4">
                  <h5>Ready to Import</h5>
                  <p className="mb-0">
                    Select a date above and click "Run Bulk Import" to process
                    payments.
                  </p>
                </div>
              </Alert>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </AppLayout>
  );
};

export default SmartupBulkImport;
